import Form, {UseDebounce} from "@autocx/forms";
import classNames from "classnames";
import {useContext, useMemo} from "react";
import {WebsiteContext} from "../../index";
import {scrollTop} from "./index";
import {normalizeProductListSort} from "./utils";
import GroupedFilterOptions from "./grouped-filter-options";

export const toggleFilters = (value, name, activeFilters, setActiveFilters, remove, productsList) => {
    const newActiveFilters = {...activeFilters};

    if (remove) {
        if (Array.isArray(newActiveFilters[name])) {
            newActiveFilters[name] = newActiveFilters[name].filter(item => item !== value);
            if (newActiveFilters[name]?.length === 0) delete newActiveFilters[name]
        } else {
            delete newActiveFilters[name]
        }
    } else {
        newActiveFilters[name] = value;
    }

    if (name !== "page" && newActiveFilters.page) delete newActiveFilters.page

    if (!newActiveFilters[name] || newActiveFilters[name]?.length === 0) delete newActiveFilters[name];

    buildParams(newActiveFilters);
    setActiveFilters(newActiveFilters)
    if (productsList?.current) scrollTop(productsList.current, "instant");
}

export const clearFilters = (activeFilters, setActiveFilters) => {
    const newActiveFilters = {}
    const sort = normalizeProductListSort(activeFilters.sort);
    if (sort) newActiveFilters.sort = sort;

    buildParams(newActiveFilters);
    setActiveFilters(newActiveFilters)
}

export const hasActiveFilters = (activeFilters) => {
    const newActiveFilters = {...activeFilters}
    if (newActiveFilters.sort) delete newActiveFilters.sort
    if (newActiveFilters.page) delete newActiveFilters.page
    const filterKeys = Object.keys(newActiveFilters);
    return filterKeys.length > 0;
}

export const buildParams = (filters, pushToHistory = true, pathname) => {
    let params = '';
    pathname = pathname || (typeof window !== 'undefined' ? window.location.pathname : '');

    for (const filter in filters) {
        let filterParam = `${filter}=`;

        if (Array.isArray(filters[filter])) {
            const separator = filter === 'sort' ? '%3B' : ';';
            filters[filter].forEach((value) => {
                filterParam += `${encodeURIComponent(String(value ?? ''))}${separator}`
            })
        } else {
            filterParam += encodeURIComponent(String(filters[filter] ?? ''))
        }

        params = params ? `${params}&${filterParam}` : filterParam;
    }

    const newUrl = params ? `${pathname}?${params}` : pathname;

    if (pushToHistory) window.history.pushState({}, '', newUrl)

    return `?${params}`;
}

const normalizeCheckboxOptions = (options = []) => {
    return (options || []).map((option) => ({
        ...option,
        label: String(option?.label ?? option?.value ?? '')
    }));
};

function GroupedFilterControl(props) {
    return (
        <div className="space-y-2">
        <p className="text-base font-semibold text-gray-700">{props.label}</p>
        <GroupedFilterOptions
            fieldId={props.fieldId}
            label={props.label}
            options={props.options}
            groups={props.groups}
            allowExpansion={props.allowExpansion}
            activeFilters={props.activeFilters}
            storeSettings={props.storeSettings}
            onChange={props.onGroupedFiltersChange}
        />
        </div>
    );
}

GroupedFilterControl.wrapWithInputGroup = false;

const getStoreFields = (storeSettings, storeOptions, activeFilters) => {
    return storeSettings?.filters?.search || storeSettings?.filters?.price || storeSettings?.filters?.discount ? [{
        fields: [
            storeSettings?.filters?.search ? {
                label: "Search",
                type: "text",
                name: 'keyword-search',
                placeholder: "What are you looking for?",
                className: "!text-base",
                required: true,
            } : null,
            storeSettings?.filters?.price ? {
                label: "Price",
                type: "control-group",
                hideFieldLabels: true,
                fields: [
                    {
                        name: 'unitPrice-min',
                        type: 'currency',
                        hidePrefix: true,
                        className: "!text-base",
                        placeholder: 'Minimum',
                    }, {
                        name: 'unitPrice-max',
                        type: 'currency',
                        hidePrefix: true,
                        className: "!text-base",
                        placeholder: 'Maximum',
                    }
                ]
            } : null,
            storeSettings?.filters?.discount ? {
                label: "Discounted Only",
                name: "discount",
                type: "switch",
                small: true,
                flipLayout: true,
            } : null,
            storeSettings?.filters?.labels && storeOptions?.labels?.length > 0 ? {
                label: "Tags",
                name: "labels",
                type: 'checkbox-list',
                className: "sm:grid-cols-1 grid-cols-2 space-y-0",
                options: normalizeCheckboxOptions(storeOptions.labels),
                value: activeFilters?.['labels']
            } : null
        ].filter(field => !!field)
    }] : []
}

export const getFieldsets = (filters, activeFilters, isBuilder = false, storeSettings, onGroupedFiltersChange) => {
    const fieldsets = [];
    filters?.forEach((filter, i) => {
        const fieldset = {
            ...filter,
            fields: [],
            collapsable: !isBuilder,
            expanded: isBuilder || i === 0,
            ...(isBuilder ? {} : {chevronClassName: "h-6 w-6", scrollIntoViewOnExpand: true}),
        };

        filter.fields.forEach(field => {
            let filter;
            if (!isBuilder && Array.isArray(field.groups) && field.groups.length > 0) {
                filter = {
                    ...field,
                    name: field.id,
                    fieldId: field.id,
                    type: "grouped-filter-options",
                    className: "sm:grid-cols-1 grid-cols-2 space-y-0",
                    options: normalizeCheckboxOptions(field.options),
                    groups: field.groups,
                    allowExpansion: storeSettings?.filterGroups?.allowExpansion === true,
                    activeFilters,
                    storeSettings,
                    onGroupedFiltersChange,
                };
            } else switch (field.type) {
                case "colour-picker":
                    filter = {
                        ...field,
                        name: field.id,
                        type: field.type,
                        className: "sm:grid-cols-1 grid-cols-2 space-y-0",
                        value: activeFilters[field.id] || [],
                    };
                    break;

                case "combobox":
                    if (isBuilder) filter = {
                        ...field,
                        name: field.id,
                        type: "checkbox-list",
                        className: "sm:grid-cols-1 grid-cols-2 space-y-0",
                        value: activeFilters[field.id] || [],
                        options: normalizeCheckboxOptions(field.options),
                    }
                    else filter = {
                        ...field,
                        name: field.id,
                        type: field.type,
                        canCreate: false,
                        required: true,
                        inputClassName: "!text-base",
                        placeholder: `Any ${field.label}`,
                        value: activeFilters[field.id] || [],
                        options: field.options || [],
                    }
                    break;

                case "number":
                    filter = {
                        ...field,
                        type: "control-group",
                        hideFieldLabels: true,
                        fields: [
                            {
                                ...field,
                                name: `${field.id}-min`,
                                id: `${field.id}-min`,
                                value: activeFilters[`${field.id}-min`],
                                type: "number",
                                min: 0,
                                placeholder: "Minimum",
                                className: "!text-base",
                                options: null,
                            },
                            {
                                ...field,
                                name: `${field.id}-max`,
                                id: `${field.id}-max`,
                                value: activeFilters[`${field.id}-max`],
                                type: "number",
                                placeholder: "Maximum",
                                className: "!text-base",
                                options: null,
                            },
                        ],
                    };
                    break;

                default:
                    filter = {
                        ...field,
                        name: field.id,
                        type: "checkbox-list",
                        className: "sm:grid-cols-1 grid-cols-2 space-y-0",
                        value: activeFilters[field.id] || [],
                        options: normalizeCheckboxOptions(field.options),
                    };
            }

            if (filter) fieldset.fields.push(filter);
        });

        if (!isBuilder) fieldsets.push({divider: true});
        fieldsets.push(fieldset);
        if (!isBuilder && i === filters.length - 1) fieldsets.push({divider: true});
    });

    return fieldsets;
};

export default function Filters({
                                    filters,
                                    className,
                                    activeFilters,
                                    setActiveFilters,
                                    categories,
                                    productsList,
                                    storeOptions,
                                    pathname
                                }) {
    const context = useContext(WebsiteContext);
    const storeSettings = context?.website?.store;
    const updateGroupedFilters = (nextActiveFilters) => {
        const newActiveFilters = {...nextActiveFilters};
        if (newActiveFilters.page) delete newActiveFilters.page;
        buildParams(newActiveFilters);
        setActiveFilters(newActiveFilters);
        if (productsList?.current) scrollTop(productsList.current, "instant");
    };
    const fieldset = getFieldsets(filters, activeFilters, false, storeSettings, updateGroupedFilters);

    const storeFields = useMemo(() => {
        return getStoreFields(context?.website?.store, storeOptions, activeFilters)
    }, [context?.website?.store, storeOptions, activeFilters]);

    const debouncedRequest = UseDebounce((newActiveFilters) => {
        buildParams(newActiveFilters);
        scrollTop(productsList.current, "instant");
    }, 300);

    return (
        <div className={classNames(className)}>
            {categories.length > 0 && (
                <div className="pb-4">
                    <h6 className="text-base font-semibold text-gray-700 group-hover:text-link">Categories</h6>
                    <div className="space-y-1">
                        {categories.map((category, i) => (
                                <div key={`${category.uuid || category._id || category.id || category.uri}_${i}`}>
                                    <a href={category.uri + buildParams(activeFilters, false, pathname)}
                                       className={classNames(
                                           category.count ? "cursor-pointer" : "pointer-events-none",
                                           "font-medium !text-inherit"
                                       )}>
                                        {category.title}
                                        <span
                                            className="ml-1.5 rounded bg-gray-200 px-1 py-0.5 text-xs font-semibold tabular-nums text-gray-600">{category.count}</span>
                                    </a>
                                </div>
                            )
                        )}
                    </div>
                </div>
            )}
            <Form
                customControls={{"grouped-filter-options": GroupedFilterControl}}
                onFieldChange={(e, name, value) => {
                    const type = e?.target?.type;
                    if (type === 'number' || type === 'text') {
                        const newActiveFilters = {...activeFilters, [name]: value};
                        if (!newActiveFilters[name]) delete newActiveFilters[name];
                        if (newActiveFilters.page) delete newActiveFilters.page
                        setActiveFilters(newActiveFilters)
                        debouncedRequest(newActiveFilters);
                    } else {
                        toggleFilters(value, name, activeFilters, setActiveFilters, false, productsList)
                    }
                }}
                values={activeFilters}
                layout={"panel-style"}
                className={"h-full list-filters"}
                fieldsets={[...storeFields, ...fieldset]}
            />
        </div>
    )
}
